import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { IgMedia } from '@core/models/ApiFields/ig-entities';
import { InstagramService } from '@core/services/admin/instagram.service';
import { FiltDimension } from '@features/social-analysis/models/IFilters';
import { ITabV2 } from '@features/social-analysis/models/ITabs';
import { insightsChartInit } from '@features/social-analysis/social-listening/config/insights-chart';
import { insightsTableInit } from '@features/social-analysis/social-listening/config/insights-table';
import {
    COMPONENTS_ENUM,
    insightsTabs,
    postTabs,
} from '@features/social-analysis/social-listening/config/page';
import { postChartInit } from '@features/social-analysis/social-listening/config/post-chart';
import { postsInit } from '@features/social-analysis/social-listening/config/posts';
import { PostUser } from '@features/social-analysis/social-listening/models/I-user';
import { AccOverview } from '@features/social-analysis/social-shared/store/acc-overview.store';
import {
    PostChart,
    SocListnInsChart,
} from '@features/social-analysis/social-shared/store/chart.store';
import { CommentsState } from '@features/social-analysis/social-shared/store/comments.store';
import { ListeningInsTable } from '@features/social-analysis/social-shared/store/ins-table.store';
import {
    PageFiltersState,
    SocialPageFilters,
} from '@features/social-analysis/social-shared/store/page-filters.store';
import { MetadataState } from '@features/social-analysis/social-shared/store/post-metadata.store';
import { PostsTable } from '@features/social-analysis/social-shared/store/table.store';
import { ToastService } from '@shared/services/toast.service';
import { PageFiltersService } from '../social-shared/services/page-filters.service';
import { SocialSharedService } from '../social-shared/services/social-shared.service';
import { CompareState } from '../social-shared/store/compare.store';
import { BaseState } from '../social-shared/store/state.store';
import { socListnFilterDims } from './config/filters';
import { PostHashtagsHttpBuilder } from './services/post-hashtags.http';
import { PostUsersHttpBuilder } from './services/post-users.http';

@Injectable({
    providedIn: 'root',
})
export class SocialListeningService {
    readonly #toastr = inject(ToastService);
    readonly #igS = inject(InstagramService);
    readonly #sharedS = inject(SocialSharedService);
    readonly #injector = inject(Injector);

    hasNoIgData = this.#sharedS.hasNoIgData;
    availableDates = this.#sharedS.availableDates;

    setInitDates = effect(() => this.#sharedS.setInitDates(this.pageFilters));

    /* STATE */

    pageFilters = new SocialPageFilters(socListnFilterDims, this.#injector);
    accountOverview = new AccOverview(this.#injector);
    comparison = new CompareState(this.#injector);
    posts = new PostsTable(postsInit, this.#injector);

    selectedPostTab = signal<ITabV2>(postTabs[0]);
    postChart = new PostChart(postChartInit, this.#injector);
    postMetadata = new MetadataState(this.#injector);
    postUsers = new BaseState<PostUser>();
    postHashtags = new BaseState<string>();
    postComments = new CommentsState(this.#injector);

    selectedInsTab = signal<ITabV2>(insightsTabs[0]);
    insChart = new SocListnInsChart(insightsChartInit, this.#injector);
    insTable = new ListeningInsTable(insightsTableInit, this.#injector);

    resetState() {
        this.pageFilters = new SocialPageFilters(socListnFilterDims, this.#injector);
        this.accountOverview = new AccOverview(this.#injector);
        this.comparison = new CompareState(this.#injector);
        this.posts = new PostsTable(postsInit, this.#injector);

        this.selectedPostTab = signal<ITabV2>(postTabs[0]);
        this.postChart = new PostChart(postChartInit, this.#injector);
        this.postMetadata = new MetadataState(this.#injector);
        this.postUsers = new BaseState<PostUser>();
        this.postHashtags = new BaseState<string>();
        this.postComments = new CommentsState(this.#injector);

        this.selectedInsTab = signal<ITabV2>(insightsTabs[0]);
        this.insChart = new SocListnInsChart(insightsChartInit, this.#injector);
        this.insTable = new ListeningInsTable(insightsTableInit, this.#injector);
    }

    /* HTTP */

    #pageFilter = inject(PageFiltersService);
    #postUsersHttp = inject(PostUsersHttpBuilder);
    #postHashtagsHttp = inject(PostHashtagsHttpBuilder);

    selPostId = computed(() => this.posts.selected()[0][IgMedia.getPrefixed('mediaId')] as string);
    selPostAcc = computed(
        () => this.posts.selected()[0][IgMedia.getPrefixed('username')] as string
    );

    insightsReq: { [key: string]: (needsTotal?: boolean) => void } = {
        [COMPONENTS_ENUM.CHART]: () => this.getInsChartData(),
        [COMPONENTS_ENUM.TABLE]: (needsTotal?: boolean) => this.getInsTableData(needsTotal),
    };

    postDetailReqs: { [key: string]: () => void } = {
        [COMPONENTS_ENUM.POST_CHART]: () => this.getPostChartData(),
        [COMPONENTS_ENUM.METADATA]: () => this.getPostMetadata(),
        [COMPONENTS_ENUM.COMMENTS]: () => this.getPostComments(),
        [COMPONENTS_ENUM.HASHTAGS]: () => this.getPostUsersAndHashtags(),
    };

    makeAllPageReqs(needsTotal?: boolean) {
        this.getAccOverview();
        this.makeFilteredComponentsReq(needsTotal);
    }

    makeFilteredComponentsReq(needsTotal?: boolean) {
        this.getComparison();
        this.getPosts(needsTotal);
        this.makeInsightsReq(needsTotal);
    }

    makeInsightsReq(needsTotal?: boolean) {
        const tab = this.selectedInsTab().value;
        this.insightsReq[tab](needsTotal);
    }

    reqPostDetail() {
        const tab = this.selectedPostTab().value;
        this.postDetailReqs[tab]();
    }

    getAccounts() {
        if (this.#sharedS.hasNoIgData())
            return this.#toastr.error('Instagram account data is missing. Please contact support');
    }

    getAccOverview() {
        const igAcc = this.#igS.igAccount()!.ig_account;
        this.accountOverview.reqData(this.pageFilters, igAcc);
    }

    getComparison() {
        const igAcc = this.#igS.igAccount()!.ig_account;
        this.comparison.reqData(this.pageFilters, igAcc);
    }

    getPosts(needsTotal?: boolean) {
        this.posts.reqData(this.pageFilters, needsTotal);
    }

    getPostChartData() {
        this.postChart.reqData(this.pageFilters, this.selPostId());
    }

    getPostMetadata() {
        this.postMetadata.reqMetadata(this.selPostId());
    }

    getPostComments() {
        const filters = {
            postId: this.selPostId(),
            userAcc: this.selPostAcc(),
        };
        this.postComments.reqData(filters);
    }

    getPostUsersAndHashtags() {
        this.#postUsersHttp.reqUsers(this.postUsers, this.selPostId());
        this.#postHashtagsHttp.reqHashtags(this.postHashtags, this.selPostId());
    }

    getInsChartData() {
        this.insChart.reqData(this.pageFilters);
    }

    getInsTableData(needsTotal?: boolean) {
        this.insTable.reqData(this.pageFilters, needsTotal);
    }

    getPageFilterOpts(state: PageFiltersState, dim: FiltDimension, query?: string | null) {
        this.#pageFilter.reqFilterOpts(state, dim, query);
    }
}
